import React, { useState, useRef, useEffect } from 'react';
import { Lead } from '../../../../types';
import { useLeadProducts } from '../../hooks/useLeadProducts';
import { PlusIcon, TrashIcon } from '../../../../components/icons';
import ProductFormModal from '../../../../components/shared/ProductFormModal';
import { Accordion } from '../../../../components/shared/Accordion';
import { ProductSelector } from './ProductSelector';
import { EditablePrice, EditableQuantity } from '../../../../components/shared/InlineEditable';
import { createModuleLogger } from '../../../../utils/logger';
import { cn } from '@/lib/utils';

const log = createModuleLogger('[LeadProductsTab]');

interface LeadProductsTabProps {
    lead: Lead;
    onUpdateValue: (value: number) => void;
}

export const LeadProductsTab: React.FC<LeadProductsTabProps> = ({ lead, onUpdateValue }) => {
    const {
        opportunityProducts,
        availableProducts,
        addProduct,
        removeProduct,
        updateProductPrice,
        updateProductQuantity,
        createProduct,
    } = useLeadProducts(lead, onUpdateValue);

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isDropdownOpen]);

    const handleSelectProduct = async (product: { id: string; name: string; value: number }) => {
        await addProduct(product.id, product.name, product.value, 1);
        setIsDropdownOpen(false);
        setIsExpanded(true);
    };

    const handleCreateNewProduct = async (productData: {
        name: string;
        value: number;
        description: string;
    }) => {
        try {
            const newProd = await createProduct(productData);
            if (newProd) {
                await addProduct(newProd.id, newProd.name, newProd.value, 1);
            }
            setIsProductModalOpen(false);
            setIsDropdownOpen(false);
            setIsExpanded(true);
        } catch (error) {
            log.error('Erro ao criar produto:', error);
        }
    };

    return (
        <section className="py-s4" aria-label="Produtos da negociação">
            <Accordion
                title={`Produtos (${opportunityProducts.length})`}
                expanded={isExpanded}
                onToggle={setIsExpanded}
                actions={
                    <div className="relative" ref={dropdownRef} onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            aria-expanded={isDropdownOpen}
                            aria-haspopup="listbox"
                            className="inline-flex min-h-11 touch-manipulation items-center gap-s1 text-xs font-semibold uppercase tracking-wide text-primary transition-colors hover:text-primary-dark md:min-h-0"
                        >
                            <PlusIcon className="size-4" aria-hidden />
                            <span>Adicionar produto</span>
                        </button>

                        <ProductSelector
                            isOpen={isDropdownOpen}
                            onClose={() => setIsDropdownOpen(false)}
                            availableProducts={availableProducts}
                            onSelect={handleSelectProduct}
                            onCreateNew={() => setIsProductModalOpen(true)}
                        />
                    </div>
                }
            >
                {opportunityProducts.length > 0 ? (
                    <ul className="m-0 list-none space-y-s2 p-0" aria-label="Lista de produtos">
                        {opportunityProducts.map((item) => (
                            <li
                                key={item.id}
                                className={cn(
                                    'group flex items-center justify-between gap-s2 rounded-lg px-s2 py-s2 text-sm',
                                    'transition-colors hover:bg-surface-subtle',
                                )}
                            >
                                <div className="flex min-w-0 items-center gap-s2 overflow-hidden pr-s2">
                                    <EditableQuantity
                                        value={item.quantity}
                                        onChange={(val) => updateProductQuantity(item.id, val)}
                                    />
                                    <span className="truncate font-medium text-default" title={item.name}>
                                        {item.name}
                                    </span>
                                </div>

                                <div className="flex shrink-0 items-center gap-s2">
                                    <EditablePrice
                                        value={item.unit_price}
                                        onChange={(val) => updateProductPrice(item.id, val)}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeProduct(item.id)}
                                        className={cn(
                                            'rounded-md p-s2 text-muted transition-all',
                                            'hover:bg-danger-bg hover:text-error',
                                            'opacity-100 focus-visible:opacity-100 md:opacity-0 md:group-hover:opacity-100',
                                        )}
                                        title="Remover produto"
                                        aria-label={`Remover ${item.name}`}
                                    >
                                        <TrashIcon className="size-3.5" aria-hidden />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="py-s4 text-center text-xs text-muted">
                        Nenhum produto adicionado ainda.
                    </p>
                )}
            </Accordion>

            <ProductFormModal
                isOpen={isProductModalOpen}
                onClose={() => setIsProductModalOpen(false)}
                onSave={handleCreateNewProduct}
            />
        </section>
    );
};
